/**
 * /customer/claim-stamp/review/:claimId — Step 3: AI Review (Phase R3).
 *
 * Mirrors mobile `(customer)/claim-stamp/review/[claimId].tsx`.
 * Lifecycle:
 *   • Poll GET /customer/receipt-claims/{id} every 2 s up to 20 s while
 *     status is `pending_ai_analysis`.
 *   • Once `pending_owner_review` (AI done) → render AI Check Complete
 *     badge, Confidence chip, extracted fields (brand/date/receipt_no/
 *     total), items list (hidden if empty), explicit pending-verification
 *     fine print, "Submit for Verification" (UX-only transition — the
 *     AI already finished server-side).
 *   • If backend auto-rejected via dedup rule (status=rejected,
 *     reason=duplicate) → forward to the status screen.
 *
 * NON-NEGOTIABLE #1: this screen MUST NEVER render "+1 Stamp Awarded"
 * or any variation. AI never awards. Only owner approval mints a stamp.
 *
 * NON-NEGOTIABLE #2: AI confidence shown prominently (chip + badge row).
 *
 * NON-NEGOTIABLE #3: items list — hide the block entirely when empty.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, CheckCircle2, Activity, Loader2, ShieldCheck,
} from 'lucide-react';

import { api, API_BASE } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { formatRupiah } from '@/lib/claimErrorUI';

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_MS = 20_000;

export default function CustomerClaimStampReviewPage() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const queryClient = useQueryClient();
  const [claim, setClaim] = useState(null);
  const [error, setError] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const pollStartRef = useRef(Date.now());
  const timerRef = useRef(null);
  const [polling, setPolling] = useState(true);

  const loadOnce = useCallback(async () => {
    if (!token || !claimId) return;
    try {
      const row = await api.customerGetReceiptClaim(token, claimId);
      setClaim(row);
      // Keep the detail cache fresh so Status page is instant.
      queryClient.setQueryData(qk.customerReceiptClaim(claimId), row);
      if (row.status === 'rejected') {
        navigate(`/customer/claim-stamp/status/${row.id}`, { replace: true });
        return;
      }
      if (row.status === 'pending_owner_review' || row.status === 'approved') {
        setPolling(false);
        return;
      }
      if (Date.now() - pollStartRef.current > POLL_MAX_MS) {
        setPolling(false);
        setError(
          'AI is taking longer than usual. The brand will still review your receipt — tap Continue to see status.',
        );
      }
    } catch (e) {
      setError(e?.detail ?? 'Could not load this claim.');
      setPolling(false);
    }
  }, [claimId, token, navigate, queryClient]);

  useEffect(() => {
    loadOnce();
  }, [loadOnce]);

  useEffect(() => {
    if (!polling) return undefined;
    timerRef.current = setInterval(loadOnce, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [polling, loadOnce]);

  // Fetch the receipt image through the authenticated endpoint and
  // turn it into an in-memory blob URL. We cannot use a plain <img src>
  // because the backend requires Bearer auth.
  useEffect(() => {
    let revoke = null;
    (async () => {
      if (!claim?.image_url || !token) return;
      try {
        const res = await fetch(`${API_BASE}${claim.image_url}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoke = url;
        setImageSrc(url);
      } catch { /* best-effort — leave image placeholder */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [claim?.image_url, token]);

  const handleSubmit = () => {
    if (!claim) return;
    navigate(`/customer/claim-stamp/status/${claim.id}`, { replace: true });
  };

  // ---- Loading shell ------------------------------------------------
  if (!claim) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3" data-testid="claim-review-loading">
        <Loader2 size={32} className="text-gold animate-spin" />
        <p className="text-sm text-white/60">Loading claim…</p>
      </div>
    );
  }

  // ---- Analyzing state (polling) ------------------------------------
  if (claim.status === 'pending_ai_analysis') {
    return (
      <div className="relative">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-gold/15 to-transparent -z-10" aria-hidden="true" />
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white"
        >
          <ChevronLeft size={14} /> Back
        </button>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-5 px-4 text-center" data-testid="claim-review-analyzing">
          <div className="w-32 h-32 rounded-full bg-gold/10 border border-gold/30 grid place-items-center">
            <Loader2 size={48} className="text-gold animate-spin" />
          </div>
          <h2 className="font-display text-2xl font-semibold">Analyzing your receipt…</h2>
          <p className="text-sm text-white/60 max-w-md">
            Our AI is reading the brand, date, items and total from your
            photo. This usually takes a few seconds.
          </p>
        </div>
      </div>
    );
  }

  // ---- Final review state --------------------------------------------
  const ai = claim.ai_extraction;
  const confidencePct = Math.round((ai?.overall_confidence ?? 0) * 100);
  const items = ai?.items ?? [];

  return (
    <div className="relative" data-testid="claim-review">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-gradient-to-b from-gold/15 to-transparent -z-10" aria-hidden="true" />

      <button
        type="button"
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white"
        data-testid="claim-review-back"
      >
        <ChevronLeft size={14} /> Back
      </button>

      <div className="mt-4 max-w-2xl">
        <div className="text-xs uppercase tracking-widest text-gold font-semibold">
          Review Receipt
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1.5">
          Confirm your details
        </h1>
      </div>

      {/* Image preview */}
      {imageSrc ? (
        <div
          className="mt-6 max-w-2xl rounded-3xl overflow-hidden border border-white/10 bg-[#15151B]"
          data-testid="claim-review-image"
        >
          <img
            src={imageSrc}
            alt="Receipt preview"
            className="w-full max-h-[480px] object-cover"
          />
        </div>
      ) : null}

      {/* Badges row — AI Check Complete + Confidence */}
      <div className="mt-4 max-w-2xl flex flex-wrap items-center gap-2">
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/35 text-emerald-300 text-xs font-semibold"
          data-testid="claim-review-ai-badge"
        >
          <CheckCircle2 size={14} />
          AI Check Complete
        </div>
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/15 border border-gold/35 text-gold text-xs font-semibold"
          data-testid="claim-review-confidence"
        >
          <Activity size={14} />
          AI Confidence: {confidencePct}%
        </div>
      </div>

      {/* Extracted fields */}
      <div
        className="mt-5 max-w-2xl rounded-3xl border border-white/8 bg-[#15151B] p-5"
        data-testid="claim-review-fields"
      >
        <FieldRow label="Brand" value={ai?.brand_name ?? '—'} />
        {ai?.outlet ? <FieldRow label="Outlet" value={ai.outlet} /> : null}
        {ai?.cashier ? <FieldRow label="Cashier" value={ai.cashier} /> : null}
        {ai?.order_type ? (
          <FieldRow
            label="Order Type"
            value={ai.order_type === 'DINE_IN' ? 'Dine In' : 'Takeaway'}
          />
        ) : null}
        {ai?.table && ai?.order_type !== 'TAKEAWAY' ? (
          <FieldRow label="Table" value={ai.table} />
        ) : null}
        <FieldRow label="Date" value={ai?.date ?? '—'} />
        <FieldRow label="Receipt No." value={ai?.receipt_no ?? '—'} mono />
        <FieldRow
          label="Total Amount"
          value={ai?.total_amount != null ? formatRupiah(ai.total_amount) : '—'}
          strong
        />
      </div>

      {/* Items list (NON-NEGOTIABLE #3: hide block if empty) */}
      {items.length > 0 ? (
        <div
          className="mt-4 max-w-2xl rounded-3xl border border-white/8 bg-[#15151B] p-5"
          data-testid="claim-review-items"
        >
          <h3 className="font-display text-base font-semibold mb-3">Items</h3>
          <ul className="space-y-2">
            {items.map((it, idx) => {
              const name = (it?.name || 'Unknown item').trim();
              const qty = it?.qty && it.qty > 1 ? ` (${it.qty})` : '';
              const price = it?.price != null ? formatRupiah(it.price) : '';
              return (
                <li
                  key={idx}
                  className="flex items-start justify-between gap-3 text-sm"
                  data-testid={`claim-review-item-${idx}`}
                >
                  <span className="text-white/85 flex-1 min-w-0">
                    <span className="text-gold mr-1.5">•</span>
                    {name}{qty}
                  </span>
                  <span className="text-white/60 font-mono shrink-0">{price}</span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {/* Pending-verification fine print — explicit NO-STAMP-YET copy */}
      <div
        className="mt-4 max-w-2xl flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/5 p-4"
        data-testid="claim-review-pending-notice"
      >
        <ShieldCheck size={16} className="text-gold mt-0.5 shrink-0" />
        <p className="text-xs text-white/70 leading-relaxed">
          <span className="font-semibold text-gold">
            Pending brand verification —
          </span>{' '}
          your stamp is awarded only after the brand approves this receipt.
          You&apos;ll be notified once it&apos;s approved.
        </p>
      </div>

      {error ? (
        <p
          className="mt-4 max-w-2xl text-sm text-red-400 text-center"
          data-testid="claim-review-error"
        >
          {error}
        </p>
      ) : null}

      {/* CTA row */}
      <div className="mt-6 max-w-2xl space-y-3">
        <button
          type="button"
          onClick={handleSubmit}
          data-testid="claim-review-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold rounded-2xl px-5 py-3.5 text-sm hover:bg-gold-light transition-colors shadow-glow-gold-sm min-h-[48px]"
        >
          Submit for Verification
        </button>
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="claim-review-back-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-transparent text-white/70 hover:text-white font-medium rounded-2xl px-5 py-3 text-sm min-h-[44px]"
        >
          Back
        </button>
      </div>
    </div>
  );
}

function FieldRow({ label, value, strong, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-white/5 last:border-b-0">
      <span className="text-sm text-white/60">{label}</span>
      <span
        className={
          'text-sm font-semibold text-right max-w-[60%] break-all ' +
          (strong ? 'text-gold text-base' : 'text-white') +
          (mono ? ' font-mono text-xs' : '')
        }
      >
        {value}
      </span>
    </div>
  );
}
